import { format, subDays } from "date-fns";
import { create } from "zustand";

type WizardStep = "user" | "week" | "lens" | "generate";

const WIZARD_STEP_USER = "user" as const;
const WIZARD_STEP_WEEK = "week" as const;
const WIZARD_STEP_LENS = "lens" as const;
const WIZARD_STEP_GENERATE = "generate" as const;

const WIZARD_STEPS: WizardStep[] = [WIZARD_STEP_USER, WIZARD_STEP_WEEK, WIZARD_STEP_LENS, WIZARD_STEP_GENERATE];

const getDefaultWeekStart = () => subDays(new Date(), 7);
const getDefaultWeekEnd = () => subDays(new Date(), 1);

interface WizardState {
  selectedUser: string;
  selectedLens: string;
  selectedLensPath: string;
  weekStart: Date;
  weekEnd: Date;
  currentStep: WizardStep;
  isGenerating: boolean;
  isAutoGenerating: boolean;

  setSelectedUser: (user: string) => void;
  setSelectedLens: (lens: string, path: string) => void;
  setWeekRange: (start: Date, end: Date) => void;
  setCurrentStep: (step: WizardStep) => void;
  setIsGenerating: (value: boolean) => void;
  setIsAutoGenerating: (value: boolean) => void;
  goToNextStep: () => void;
  goToPreviousStep: () => void;
  resetWizard: () => void;
  getWeekTimeRange: () => string;
  getStepIndex: () => number;
}

const initialState = {
  selectedUser: "",
  selectedLens: "",
  selectedLensPath: "",
  weekStart: getDefaultWeekStart(),
  weekEnd: getDefaultWeekEnd(),
  currentStep: WIZARD_STEP_USER as WizardStep,
  isGenerating: false,
  isAutoGenerating: false
};

export const useWizardStore = create<WizardState>((set, get) => ({
  ...initialState,

  setSelectedUser: (user: string) => set({ selectedUser: user, selectedLens: "", selectedLensPath: "" }),

  setSelectedLens: (lens: string, path: string) => set({ selectedLens: lens, selectedLensPath: path }),

  setWeekRange: (start: Date, end: Date) => set({ weekStart: start, weekEnd: end, selectedLens: "", selectedLensPath: "" }),

  setCurrentStep: (step: WizardStep) => set({ currentStep: step }),

  setIsGenerating: (value: boolean) => set({ isGenerating: value }),

  setIsAutoGenerating: (value: boolean) => set({ isAutoGenerating: value }),

  goToNextStep: () => {
    const { currentStep } = get();
    const currentIndex = WIZARD_STEPS.indexOf(currentStep);
    if (currentIndex < WIZARD_STEPS.length - 1) {
      set({ currentStep: WIZARD_STEPS[currentIndex + 1] });
    }
  },

  goToPreviousStep: () => {
    const { currentStep } = get();
    const currentIndex = WIZARD_STEPS.indexOf(currentStep);
    if (currentIndex > 0) {
      set({ currentStep: WIZARD_STEPS[currentIndex - 1] });
    }
  },

  resetWizard: () => set({
    ...initialState,
    weekStart: getDefaultWeekStart(),
    weekEnd: getDefaultWeekEnd()
  }),

  getWeekTimeRange: () => {
    const { weekStart, weekEnd } = get();
    return `custom:${format(weekStart, "yyyy-MM-dd")}:${format(weekEnd, "yyyy-MM-dd")}`;
  },

  getStepIndex: () => {
    const { currentStep } = get();
    return WIZARD_STEPS.indexOf(currentStep);
  }
}));

export { WIZARD_STEPS, WIZARD_STEP_USER, WIZARD_STEP_WEEK, WIZARD_STEP_LENS, WIZARD_STEP_GENERATE };
export type { WizardStep };
